import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { cn } from "@/components/podkit/lib/cn";
import type { UserMessage } from "@/hooks/use-stream-conversation";
import { IconClock } from "central-icons/IconClock";
import { type FC } from "react";

// The ralph command prefix - matches "/ona:ralph" with or without trailing content
const RALPH_COMMAND = "/ona:ralph";

/**
 * Strips the /ona:ralph prefix from content.
 * Handles both "/ona:ralph message" (with space) and "/ona:ralph" (command only).
 */
function stripRalphPrefix(content: string): { isRalphMode: boolean; content: string } {
    if (content.startsWith(RALPH_COMMAND)) {
        const remainder = content.slice(RALPH_COMMAND.length);
        // Handle both "/ona:ralph message" and "/ona:ralph" (no message)
        if (remainder === "" || remainder.startsWith(" ")) {
            return { isRalphMode: true, content: remainder.trimStart() };
        }
    }
    return { isRalphMode: false, content };
}

/**
 * Checks if content starts with the ralph command.
 */
function hasRalphCommand(content: string): boolean {
    if (!content.startsWith(RALPH_COMMAND)) {
        return false;
    }
    const remainder = content.slice(RALPH_COMMAND.length);
    // Must be exactly "/ona:ralph" or "/ona:ralph " followed by content
    return remainder === "" || remainder.startsWith(" ");
}

export const UserMsg: FC<{
    message: UserMessage;
    isPending: boolean;
}> = ({ message, isPending }) => {
    // Check if any fragment starts with /ona:ralph
    const hasRalphPrefix = message.fragments.some(
        (fragment) => fragment.type === "text" && hasRalphCommand(fragment.content),
    );

    return (
        // the class user-message is used within the markdown component to style some markdown elements differently when presented
        // inside a user message.
        <div
            className="user-message flex w-fit max-w-full gap-2 self-end"
            data-message-id={`${message.type}-${message.id}`}
        >
            <div
                className={cn(
                    "min-w-0 flex-1 rounded-xl border border-border-subtle bg-surface-muted px-3 py-2 text-base text-content-primary transition-opacity",
                    {
                        "opacity-50": isPending,
                    },
                )}
            >
                <div className="flex gap-2">
                    {isPending && <IconClock className="mt-[1px] shrink-0 text-content-primary" size="14" />}
                    {hasRalphPrefix && <IconRalphWiggum size={20} className="mt-[1px] shrink-0" />}
                    <div className="flex min-w-0 flex-1 flex-col">
                        {message.fragments.map((fragment) => {
                            switch (fragment.type) {
                                case "text": {
                                    const { content } = stripRalphPrefix(fragment.content);
                                    return <Markdown content={content} key={fragment.id} />;
                                }
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
